import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useAxios, APIError } from '../../core/_axios';
import { PhysicalBook } from '../../core/_models';
import { getBookByUniqueIdentifier } from '../../core/_requests';
import usePageTitle from '../../hooks/usePageTitle';
import { OrderBookForm } from './OrderBookForm';
import { CONTAINER_CONTENT_CLASS } from '../signatures/Signatures';

export function ManageBookRoutes() {
    const axios = useAxios();

    const {
        pathname
    } = useLocation();

    const params = useParams();

    usePageTitle('Manage Book');

    const [loading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState<string | null>(null);

    const [book, setBook] = useState<PhysicalBook | null>(null);

    const linkToViewBook = useMemo(() => {
        if (!book) {
            return '';
        }
        return `${process.env.NODE_ENV === 'development' ? 'https://developers.sandbox.lulu.com' : 'https://developers.lulu.com'}/print-jobs/detail/${book.public_data.lulu_order_id}`
    }, [book]);

    const loadBook = useCallback(async () => {
        if (!params.bookUniqueId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        setLoadingError(null);
        try {
            const updatedBook = await getBookByUniqueIdentifier(axios, params.bookUniqueId);
            setBook(updatedBook);
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
            setLoadingError(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [axios, params.bookUniqueId]);

    useEffect(() => {
        loadBook();
    }, [loadBook]);

    if (loading) {
        return (
            <div className="container-fixed">
                <LoadingSpinner />
            </div>
        );
    }

    if (loadingError) {
        return (
            <div className="container-fixed">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <span className="block sm:inline">
                        {loadingError}
                    </span>
                </div>
                <div className="mt-5">
                    <button className="btn btn-lg btn-light" onClick={loadBook}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    if (book.public_data.status === 'ordered') {
        if (!linkToViewBook) {
            return (
                <div className="container-fixed">
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                        <span className="block sm:inline">
                            This book has been ordered but the link to view the order is not available.
                        </span>
                    </div>
                    <div className="mt-5">
                        <button className="btn btn-lg btn-light" onClick={loadBook}>
                            Retry
                        </button>
                    </div>
                </div>
            );
        }
        return (
            <div className="container-fixed">
                <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded relative" role="alert">
                    <span className="block sm:inline">
                        This book has been ordered. Click the button below to view the order.
                    </span>
                </div>
                <div className="mt-5">
                    <a href={linkToViewBook} className="btn btn-lg btn-light" target="_blank" rel="noreferrer">
                        View Order
                    </a>
                </div>
            </div>
        );
    }

    if (book.public_data.status !== 'ready') {
        return (
            <div className="container-fixed">
                <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded relative" role="alert">
                    <span className="block sm:inline">
                        This book is still being prepared.
                    </span>
                </div>
                <div className="mt-5">
                    <button className="btn btn-lg btn-light" onClick={loadBook}>
                        Reload
                    </button>
                </div>
            </div>
        );
    }

    return <>
        <div className="container-fixed">
            <div className={CONTAINER_CONTENT_CLASS}>
                <div className="flex flex-col justify-center gap-2">
                    <h1 className="text-xl font-semibold leading-none text-gray-900">
                        Manage Book
                    </h1>
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
                        {/* subtitle? */}
                    </div>
                </div>
                {false && <div className="flex items-center gap-2.5">
                    <a className="btn btn-sm btn-light" href="#">
                        Go to Teams
                    </a>
                </div>}
            </div>
        </div>
        <div className="container-fixed">
            {false && "nav..." && <div className='mb-5'>
                <ul className="flex flex-wrap text-sm font-medium text-center text-gray-500 border-b border-gray-200 dark:border-gray-700 dark:text-gray-400">
                    <li className="me-2">
                        <Link to={`/books/${book.unique_identifier}`}
                            className={[
                                pathname === `/books/${book.unique_identifier}/details` ?
                                    'text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-gray-800 dark:text-blue-500' :
                                    'text-gray-500 dark:text-gray-400',
                                'inline-block p-4'
                            ].join(' ')}>
                            Details
                        </Link>
                    </li>
                </ul>
            </div>}
            <Routes>
                <Route path="/details" element={
                    <OrderBookForm
                        book={book}
                    />}
                />
                <Route
                    path="*"
                    element={<Navigate
                        to={`/books/${book.unique_identifier}/details`}
                        replace
                    />}
                />
            </Routes>
        </div>
    </>
}